"use client";

import { ExternalLink, X } from "lucide-react";
import Image from "next/image";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type { JanoshikReport } from "@/config/janoshik-reports";
import { JANOSHIK_LOGO_SRC } from "@/config/janoshik-reports";
import { formatDate, type Locale } from "@/config/locale";

interface JanoshikReportModalProps {
	report: JanoshikReport;
	onClose: () => void;
}

export function JanoshikReportModal({ report, onClose }: JanoshikReportModalProps) {
	const t = useTranslations("janoshik.modal");
	const format = useFormatter();
	const locale = useLocale();
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = previousOverflow;
		};
	}, [onClose]);

	const copyVerifyKey = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(report.verifyKey);
			setCopied(true);
			setTimeout(() => setCopied(false), 1800);
		} catch {
			// clipboard may be unavailable; ignore
		}
	}, [report.verifyKey]);

	const fmtDate = (iso: string) =>
		formatDate(new Date(iso), { day: "numeric", month: "long", year: "numeric" }, locale as Locale);
	const fmtPurity = format.number(report.purityPercent, {
		maximumFractionDigits: 3,
		minimumFractionDigits: 3,
	});
	const fmtActive = format.number(report.activeSubstanceMg, {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	});

	if (typeof document === "undefined") return null;

	return createPortal(
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="janoshik-modal-title"
			onClick={onClose}
			className="bg-foreground/70 fixed inset-0 z-[9999] grid place-items-center p-4 backdrop-blur-sm"
		>
			<div
				onClick={(e) => e.stopPropagation()}
				className="relative grid max-h-[92dvh] w-full max-w-[1080px] grid-cols-1 overflow-auto rounded-2xl bg-card shadow-2xl md:grid-cols-[1fr_360px] md:overflow-hidden"
			>
				<div className="relative bg-muted p-4 pt-16 md:overflow-auto md:p-6">
					<Image
						src={report.reportImageSrc}
						alt={`Janoshik test report ${report.productLabel}`}
						width={1600}
						height={2360}
						sizes="(min-width: 1080px) 720px, 100vw"
						className="block h-auto w-full rounded shadow-md"
					/>
				</div>

				<div className="flex flex-col gap-4 border-t border-border p-6 md:overflow-auto md:border-l md:border-t-0">
					<div className="flex items-start justify-between">
						<Image
							src={JANOSHIK_LOGO_SRC}
							alt="Janoshik Analytical"
							width={92}
							height={56}
							className="h-7 w-auto"
						/>
						<button
							onClick={onClose}
							aria-label={t("closeLabel")}
							className="fixed right-6 top-6 z-10 grid h-11 w-11 place-items-center rounded-md border border-border bg-card text-foreground shadow-sm hover:bg-muted md:absolute md:right-4 md:top-4"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					</div>

					<div>
						<p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
							{t("eyebrow")}
						</p>
						<h3 id="janoshik-modal-title" className="text-xl font-extrabold tracking-tight text-foreground">
							{report.productLabel}
						</h3>
						<p className="font-mono text-xs text-muted-foreground">Task #{report.taskNumber}</p>
					</div>

					<div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border">
						<div className="bg-card p-4">
							<p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
								{t("purityLabel")}
							</p>
							<p className="text-2xl font-extrabold tracking-tight text-accent-dark">{fmtPurity}&nbsp;%</p>
							<p className="text-[10px] text-muted-foreground">{t("purityUnit")}</p>
						</div>
						<div className="bg-card p-4">
							<p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
								{t("activeLabel")}
							</p>
							<p className="text-2xl font-extrabold tracking-tight text-foreground">{fmtActive}&nbsp;mg</p>
							<p className="text-[10px] text-muted-foreground">
								{t("activeUnit", { declared: report.declaredMg })}
							</p>
						</div>
					</div>

					<dl className="text-xs">
						{[
							[t("manufacturerLabel"), t("manufacturerValue")],
							[t("sampleLabel"), t("sampleValue")],
							[t("testsLabel"), report.testDescription],
							[t("orderedLabel"), fmtDate(report.orderedAt)],
							[t("receivedLabel"), fmtDate(report.receivedAt)],
							[t("analyzedLabel"), fmtDate(report.analyzedAt)],
						].map(([label, value], i, arr) => (
							<div
								key={label}
								className={`grid grid-cols-[auto_1fr] gap-2 py-2 ${
									i < arr.length - 1 ? "border-border/50 border-b" : ""
								}`}
							>
								<dt className="font-medium text-muted-foreground">{label}</dt>
								<dd className="text-right font-semibold text-foreground">{value}</dd>
							</div>
						))}
					</dl>

					<div className="rounded-xl border border-[var(--accent)] bg-accent-light p-3">
						<p className="text-[10px] font-bold uppercase tracking-wider text-accent-dark">
							{t("verifyKeyLabel")}
						</p>
						<div className="mt-1 flex items-center gap-2">
							<code className="flex-1 font-mono text-sm font-bold text-foreground">{report.verifyKey}</code>
							<button
								onClick={copyVerifyKey}
								className="rounded-md border border-[var(--accent)] bg-card px-2.5 py-1 text-[11px] font-bold text-accent-dark hover:bg-accent-light"
							>
								{copied ? t("copied") : t("copy")}
							</button>
						</div>
					</div>

					<div className="mt-auto grid gap-2 pt-2">
						<a
							href={report.verifyUrl}
							target="_blank"
							rel="nofollow noopener noreferrer external"
							className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-dark px-4 py-2.5 text-sm font-bold text-card transition-colors hover:opacity-90"
						>
							{t("verifyOnJanoshik")}
							<ExternalLink className="h-3.5 w-3.5" />
						</a>
						<a
							href={report.reportImageSrc}
							download={`janoshik-${report.productSlug}-${report.taskNumber}.png`}
							className="rounded-md border border-border bg-card px-4 py-2 text-center text-xs font-semibold text-foreground hover:bg-muted"
						>
							{t("downloadReport")}
						</a>
					</div>

					<p className="text-[10px] leading-relaxed text-muted-foreground">{t("disclaimer")}</p>
				</div>
			</div>
		</div>,
		document.body,
	);
}
